import { Inventory2Outlined } from '@mui/icons-material'
import Close from '@mui/icons-material/Close'
import { Delete, Star } from '@mui/icons-material'
import { Divider, IconButton, ListItemText, MenuItem, Popover, useMediaQuery } from '@mui/material'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { changes } from 'src/components/utils/fields'
import { M_Button, M_CheckBox, M_Fieldset, M_FormFavorites, M_InputTextField, M_SimpleTable, useFavorites } from 'src/M_Components'
import { QuoteFormField } from './QuoteFormField'
import { revisionCellStyle } from '../utils/quoteRevisionHelpers'
import { QuoteTablePager } from './QuoteTablePager'
import { usePagedRows } from '../utils/usePagedRows'
import { hasGoodsDescription } from '../utils/quoteValidation'

const GOODS_PAGE_SIZE = 5

function GroupFavoriteActions({ groupName, t }) {
  const favCtx = useFavorites()
  const [anchorEl, setAnchorEl] = useState(null)
  const [name, setName] = useState('')

  if (!favCtx) return null

  const { saveGroupFavorite, getGroupFavorites, handleApplyFavorite, handleDeleteFavorite } = favCtx
  const groupFavs = getGroupFavorites(groupName)

  return (
    <div style={{ position: 'absolute', top: '10px', right: '18px', zIndex: 10 }}>
      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
        <Star sx={{ color: groupFavs.length > 0 ? 'var(--color-yellow, #f9a825)' : '#ccc', fontSize: '20px' }} />
      </IconButton>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => { setAnchorEl(null); setName('') }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <div style={{ minWidth: '250px' }}>
          <div style={{ padding: '12px', display: 'flex', gap: '8px', alignItems: 'center' }}>
            <M_InputTextField
              size="small"
              label={t('FavoriteName') || 'Name'}
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && name.trim()) {
                  saveGroupFavorite(groupName, name)
                  setName('')
                }
              }}
              autoFocus
              fullWidth
            />
            <M_Button
              normal
              hoverEffect
              label={t('Save') || 'Save'}
              backgroundColor={'var(--color-blue)'}
              labelColor="var(--color-white)"
              onClick={() => {
                if (name.trim()) {
                  saveGroupFavorite(groupName, name)
                  setName('')
                }
              }}
            />
          </div>
          {groupFavs.length > 0 && <Divider />}
          {groupFavs.map(fav => (
            <MenuItem
              key={fav.Id}
              onClick={() => { handleApplyFavorite(fav); setAnchorEl(null) }}
              sx={{
                '& .fav-delete': { opacity: 0, transition: 'opacity 0.2s' },
                '&:hover .fav-delete': { opacity: 1 }
              }}
            >
              <ListItemText>{fav.Description}</ListItemText>
              <IconButton
                className="fav-delete"
                size="small"
                onClick={(e) => { e.stopPropagation(); handleDeleteFavorite(fav.Id, e) }}
                sx={{ marginLeft: '8px' }}
              >
                <Delete fontSize="small" sx={{ color: 'var(--color-red, #d32f2f)' }} />
              </IconButton>
            </MenuItem>
          ))}
        </div>
      </Popover>
    </div>
  )
}

export const QuoteGoods = ({ dataState, setDataState, requestMode = false }) => {
  const { t } = useTranslation()

  const [isMultiEditable, setIsMultiEditable] = useState(!dataState.isMultiEdit)
  const fieldsEditable = !dataState.isRevisionView && isMultiEditable

  const goodsVarNameDict = useMemo(() => new Map([
    ['commodityCode',        dataState.commodityCode        || ''],
    ['commodityName',        dataState.commodityName        || ''],
    ['commodityId',          dataState.commodityId          || ''],
    ['dangerousGoodsCode',   dataState.dangerousGoodsCode   || ''],
    ['dangerousGoodsName',   dataState.dangerousGoodsName   || ''],
    ['dangerousGoodsId',     dataState.dangerousGoodsId     || ''],
    ['dangerousGoodsClass',  dataState.dangerousGoodsClass  || ''],
    ['grossWeight',          dataState.grossWeight          || ''],
    ['measures',             dataState.measures             || ''],
    ['goodsDescription',     dataState.goodsDescription     || ''],
  ]), [
    dataState.commodityCode, dataState.commodityName, dataState.commodityId,
    dataState.dangerousGoodsCode, dataState.dangerousGoodsName, dataState.dangerousGoodsId, dataState.dangerousGoodsClass,
    dataState.grossWeight, dataState.measures, dataState.goodsDescription,
  ])

  const matches = useMediaQuery('(min-width:1800px)')
  const matches2 = useMediaQuery('(min-width:1700px)')
  const matches3 = useMediaQuery('(min-width:1490px)')
  const matches4 = useMediaQuery('(min-width:1450px)')
  const matches5 = useMediaQuery('(min-width:1420px)')
  const matches6 = useMediaQuery('(min-width:1380px)')
  const matches7 = useMediaQuery('(min-width:1280px)')
  const matches8 = useMediaQuery('(min-width:1230px)')
  const matches9 = useMediaQuery('(min-width:1190px)')
  const matches10 = useMediaQuery('(min-width:1150px)')
  const matches11 = useMediaQuery('(min-width:970px)')
  const matches12 = useMediaQuery('(min-width:930px)')
  const matches13 = useMediaQuery('(min-width:850px)')
  const matches14 = useMediaQuery('(min-width:870px)')
  const matches15 = useMediaQuery('(min-width:615px)')
  const matches16 = useMediaQuery('(min-width:440px)')

  const allGoods = (dataState['Goods'] && dataState['Goods']) || []
  const goodsDescriptionRequired = !hasGoodsDescription(dataState)

  const formFields = [
    {
      display: t('CommodityCode'),
      code: 'commodityCode',
      type: 'foreignKey',
      entity: 'Commodity',
      filterField: 'CommodityCode',
      labelFields: ['CommodityCode'],
      fields: [
        { key: 'CommodityCode', value: 'commodityCode' },
        { key: 'CommodityName', value: 'commodityName' },
        { key: 'Id', value: 'commodityId' },
      ],
      favoritesFieldName: t('CommodityCode'),
      favoritesGroupName: 'Goods',
      favoritesVarKey: 'commodityCode',
      favoritesIdKey: 'commodityId',
    },
    {
      display: t('CommodityName'),
      code: 'commodityName',
      type: 'disabled',
      favoritesFieldName: t('CommodityName'),
      favoritesGroupName: 'Goods',
      favoritesVarKey: 'commodityName',
    },
    {
      display: t('UNNo'),
      code: 'dangerousGoodsCode',
      type: 'foreignKey',
      entity: 'DangerousGoods',
      filterField: 'Code',
      labelFields: ['Code', 'Name'],
      fields: [
        { key: 'Id', value: 'dangerousGoodsId' },
        { key: 'Code', value: 'dangerousGoodsCode' },
        { key: 'Name', value: 'dangerousGoodsName' },
        { key: 'Class', value: 'dangerousGoodsClass' },
      ],
      favoritesFieldName: t('UNNo'),
      favoritesGroupName: 'Goods',
      favoritesVarKey: 'dangerousGoodsCode',
      favoritesIdKey: 'dangerousGoodsId',
    },
    { display: t('GrossWeight'), code: 'grossWeight', type: 'number', favoritesFieldName: t('GrossWeight'), favoritesGroupName: 'Goods', favoritesVarKey: 'grossWeight' },
    ...(!requestMode ? [{ display: t("Measures"), required: false, code: "measures", type: "text", favoritesFieldName: t('Measures'), favoritesGroupName: 'Goods', favoritesVarKey: 'measures' }] : []),
    { display: t("GoodsDescription"), required: goodsDescriptionRequired, code: "goodsDescription", type: "text", favoritesFieldName: t('GoodsDescription'), favoritesGroupName: 'Goods', favoritesVarKey: 'goodsDescription' },
    { type: 'whitespace' },
    // { type: !matches4 ? '' : 'whitespace' },
    // { type: !matches4 ? '' : 'whitespace' },
  ]

  const addGoods = () => {
    let counter = dataState?.GoodsCounter || 1
    let newObj = {}
    formFields.forEach((obj) => {
      newObj[obj.code] = dataState?.[obj.code] || ''
      changes(obj.code, '', setDataState)
      if (obj.labelFields) {
        obj.fields.forEach((extra) => {
          newObj[extra.value] = dataState[extra.value] || ''
          changes(extra.value, '', setDataState)
        })
      }
    })

    const newGoods = [...(dataState?.Goods || [])]
    newObj.Id = counter
    newGoods.push(newObj)
    changes('GoodsCounter', counter + 1, setDataState)
    changes('Goods', newGoods, setDataState)
  }

  const goodsPager = usePagedRows(allGoods, GOODS_PAGE_SIZE)

  const deleteGoods = (row) => {
    const newGoods = dataState?.Goods.filter((good) => good.Id !== row.Id)
    changes('Goods', newGoods, setDataState)
  }

  return (
    <M_FormFavorites
      entityName="Quotes"
      pageUrl="Quote"
      varNameDict={goodsVarNameDict}
      setVarNameDict={() => {}}
      onFavoriteLoad={(loadedData) => {
        setDataState(prev => ({ ...prev, ...loadedData }))
      }}
      onAfterApply={() => {}}
    >
      <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <GroupFavoriteActions groupName="Goods" t={t} />
      <M_Fieldset
        height="100%"
        labelInside
        label={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              fontSize: '25px',
              marginLeft: '10px',
            }}
          >
            <Inventory2Outlined style={{ marginRight: '10px', scale: '1.3' }} />{' '}
            {t('Goods')}
            {!dataState.isRevisionView && dataState.isMultiEdit && <M_CheckBox checked={isMultiEditable} onChange={(e) => {setIsMultiEditable(e.target.checked)}} />}
          </div>
        }
        elements={
          <div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns:
                  (!matches15 && 'auto') ||
                  (!matches9 && 'auto auto') ||
                  (!matches4 && 'auto auto auto') ||
                  'auto auto auto auto',
                justifyContent: 'space-evenly',
              }}
            >
              {formFields.map((field, index) => 
                <QuoteFormField 
                  fields={field} 
                  index={index} 
                  data={dataState} 
                  setData={setDataState} 
                  disabled={!fieldsEditable} 
                />
              )}

              {!dataState.isRevisionView && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <M_Button
                  bold
                  normal
                  hoverEffect
                  label={t('Add')}
                  icon={<img src='/icons/svgsDaNet/plus-square.svg' alt={t('Add')} style={{ filter: 'invert(42%) sepia(44%) saturate(4434%) hue-rotate(74deg) brightness(97%) contrast(101%)' }} className='iconCreateBooking' />}
                  borderColor={'var(--color-grey)'}
                  onClick={() => addGoods()}
                  disable={!fieldsEditable}
                />
              </div>
              )}
            </div>
            <div style={{ marginBottom: '20px' }}></div>
            <M_SimpleTable
              rows={goodsPager.pagedRows}
              columns={[
                ...formFields
                  .map((obj) => {
                    return {
                      header: obj.display,
                      field: obj.code,
                      body: (row) => {
                        const rowIndex = allGoods.findIndex(
                          (item) => item.Id === row.Id,
                        );
                        return (
                          <span
                            style={revisionCellStyle(
                              dataState,
                              "goods",
                              rowIndex,
                              obj.code,
                            )}
                          >
                            {row[obj.code] ?? ""}
                          </span>
                        );
                      },
                    };
                  })
                  .filter(
                    (obj) => obj.type !== 'whitespace' || obj.type !== '',
                  ),
                ...(!dataState.isRevisionView
                  ? [{
                  header: '',
                  body: (row) => (
                    <M_Button
                      none
                      icon={<Close style={{ color: "var(--color-red)" }} />}
                      onClick={() => deleteGoods(row)}
                    />
                  ),
                }]
                  : []),
              ]}
            />
            <QuoteTablePager {...goodsPager} />
          </div>
        }
      />
      </div>
    </M_FormFavorites>
  )
}
